import { getAxisFormatterWithTimeFallback } from './get-axis-formatter-with-time-fallback.js';
import {
  getMagnifierLabel,
  MagnifierFormatter,
} from '../../../../core/components/crosshair/utils/get-magnifier-label.js';
import {
  FormatterWithPrecision,
  TimeFormatter,
} from '../../../../core/types/formatter.js';
import {
  AxisIdToFormatter,
  XYChartAxisInternal,
  XYChartVisibleAxesConfig,
  XYChartVisibleAxesScales,
} from '../../../types/xy-chart-internals.js';

type AXIS = 'left' | 'right' | 'bottom' | 'top';
type AXES = AXIS[];
type Formatters = [AXIS, MagnifierFormatter][];
type LabelsAccumulator = {
  left: string | undefined;
  right: string | undefined;
  bottom: string | undefined;
  top: string | undefined;
};
type GetFormatter = (
  axis: AXIS,
) => FormatterWithPrecision | TimeFormatter | undefined;
type GetLabel = (
  axis: AxisWithFormatter[0],
  formatter: AxisWithFormatter[1],
) => string | undefined;

type AxisWithFormatter = Formatters[number];

const coordinates = {
  top: 'x',
  bottom: 'x',
  left: 'y',
  right: 'y',
} as const;

const defaultLabels = () => ({
  left: undefined,
  right: undefined,
  bottom: undefined,
  top: undefined,
});

type PartialAxesConfig = {
  [key in keyof XYChartVisibleAxesConfig]:
    | Pick<XYChartAxisInternal, 'id' | 'formatter' | 'type' | 'unit'>
    | undefined;
};

export type ChartCrosshairDataTransformer = (args: {
  position: { x: number; y: number };
  pinned: boolean;
  axesScale: XYChartVisibleAxesScales;
  axesConfig: PartialAxesConfig;
  axesIdToFormatter: AxisIdToFormatter;
  width: number;
  axes?: AXES;
}) => {
  labels: LabelsAccumulator;
};

const accumulateFormatters =
  (getFormatter: GetFormatter) => (acc: Formatters, axis: AXIS) => {
    const formatter = getFormatter(axis);

    if (formatter) {
      acc.push([axis, formatter]);
    }
    return acc;
  };

const accumulateLabels =
  (getLabel: GetLabel) =>
  (acc: LabelsAccumulator, axisWithFormatter: AxisWithFormatter) => {
    const [axis, formatter] = axisWithFormatter;
    const label = getLabel(axis, formatter);

    if (label) {
      acc[axis] = label;
    }

    return acc;
  };

export const chartCrosshairDataTransformer: ChartCrosshairDataTransformer = (
  args,
) => {
  const { position, pinned, axesScale, axesConfig, axesIdToFormatter, width } =
    args;
  const axes = args.axes || ['left', 'right', 'bottom', 'top'];

  if (pinned) {
    return {
      labels: defaultLabels(),
      _formatters: [],
      _labelsFor: (_f: Formatters) => defaultLabels(),
    };
  }

  const getFormatter: GetFormatter = (axis: AXIS) => {
    const axisConfig = axesConfig[axis];
    const scale = axesScale[axis];
    if (axisConfig) {
      return getAxisFormatterWithTimeFallback({
        axisConfig,
        axisId: axisConfig.id,
        scale,
        axesIdToFormatter,
        width,
      });
    }
  };

  const getLabel: GetLabel = (axis, formatter) => {
    return getMagnifierLabel(
      axesConfig[axis]?.unit,
      coordinates[axis],
      formatter,
      position,
      axesScale[axis],
    );
  };

  const formatters = axes.reduce(accumulateFormatters(getFormatter), []);

  const labelsFor = (formatters: Formatters) =>
    formatters.reduce(accumulateLabels(getLabel), defaultLabels());

  const labels = labelsFor(formatters);

  return {
    _formatters: formatters,
    _labelsFor: labelsFor,
    labels,
  };
};
